import React from 'react'
import _ from 'lodash/fp'
import { useLazyQuery, useMutation } from 'react-apollo'

import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import Dialog from '@material-ui/core/Dialog'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import List from '@material-ui/core/List'

import SearchIcon from '@material-ui/icons/Search'
import AddIcon from '@material-ui/icons/Add'

import { makeStyles } from '@material-ui/core/styles'

import colors from '@fe/ui/colors'

import { SEARCH_FOR_BUDDY, SEND_BUDDY_REQUEST } from './buddiesQueries'
import Buddy from './Buddy'

const SearchBox = ({ loading, ...props }) => (
  <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
    <TextField
      fullWidth
      label='Search for user by name'
      autoFocus
      {...props}
    />
    <div style={{ color: colors.primaryLightGreen, marginLeft: 16, display: 'flex', alignSelf: 'flex-end' }}>
      {loading
        ? <CircularProgress size={32} />
        : <SearchIcon fontSize='large' />}
    </div>
  </div>
)

const useButtonStyles = makeStyles(theme => ({
  label: {
    color: isCancelButton => isCancelButton ? '#999999' : colors.primaryLightGreen
  },
  root: {
    marginRight: isCancelButton => isCancelButton && 'auto'
  }
}))

const useStyles = makeStyles(theme => ({
  actions: {
    paddingBottom: 16,
    paddingTop: 16,
    display: 'flex',
    justifyContent: 'flex-end'
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    padding: 32,
    paddingBottom: 0,
    overflow: 'hidden',
    [theme.breakpoints.up('sm')]: {
      paddingBottom: 0,
      padding: 64
    }
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between'
  }
}))

const AddBuddyModal = props => {
  const [query, setQuery] = React.useState('')
  const [isLoading, setIsLoading] = React.useState(false)
  const [searchForBuddyQuery, { data, called }] = useLazyQuery(SEARCH_FOR_BUDDY, { onCompleted: () => setIsLoading(false) })

  const [sendBuddyRequest] = useMutation(SEND_BUDDY_REQUEST)

  const searchForBuddy = React.useCallback(_.debounce(500, query => {
    searchForBuddyQuery({ variables: { query } })
  }), [])

  React.useEffect(() => {
    if (_.size(query) > 1) {
      setIsLoading(true)
      searchForBuddy(query)
    }
  }, [query])

  const buttonClasses = useButtonStyles(false)
  const cancelButtonClasses = useButtonStyles(true)
  const classes = useStyles()

  const searchResult = _.get('searchForBuddy', data)
  const noResults = _.isEmpty(searchResult) && called && !isLoading
  return (
    <Dialog
      open={props.open}
      onBackdropClick={props.handleClose}
      onEscapeKeyDown={props.handleClose}
      PaperProps={{ square: true, style: { flexDirection: 'column', display: 'flex' } }}
      maxWidth='md'
    >
      <div className={classes.container}>
        <div className={classes.body}>
          <Typography variant='h4'>
            Find your Buddies
          </Typography>
          <Typography variant='subtitle1'>
            If your Buddy already has a GLFR user, you can search for them below. If not, you can either invite them to join GLFR for free or add them as a Local Buddy
          </Typography>
          <SearchBox
            value={query}
            onChange={e => setQuery(_.get('target.value', e))}
            loading={isLoading}
          />
          {noResults && 'We could not find a user with that name'}
          <List style={{ maxHeight: 500, overflow: 'auto' }}>
            {_.map(buddy => (
              <Buddy
                key={_.get('id', buddy)}
                shouldLink={false}
                profile={buddy}
                primary={{
                  action: () => sendBuddyRequest({ variables: { to: buddy.id } }).then(() => props.handleClose()),
                  icon: <AddIcon fontSize='large' style={{ color: colors.secondaryDarkGrey }} />
                }}
              />
            ), _.take(20, searchResult))}
          </List>
          <div className={classes.actions}>
            <Button onClick={props.handleClose} classes={cancelButtonClasses}>CANCEL</Button>
            {(noResults || !_.isEmpty(searchResult)) && (<Button onClick={props.handleAddLocalBuddy} classes={buttonClasses}>ADD LOCAL BUDDY</Button>)}
            {(noResults || !_.isEmpty(searchResult)) && (<Button onClick={props.handleInviteByEmail} classes={buttonClasses}>INVITE BY EMAIL</Button>)}
          </div>
        </div>
      </div>
    </Dialog>
  )
}

export default AddBuddyModal
